import React, { startTransition } from 'react'
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material'
import { useState, useMemo, useActionState } from 'react'
import { ColumnConfigurationIcon, DownloadIcon } from '../../../../assets/icons'
import {
  CommonModal,
  CommonTable,
  ConfirmationDialog,
  Search,
} from '../../../common'
import KeyFilter from '../common/KeyFilter.jsx'
import { columns } from '../configs/duplicationRuleConfig.jsx'
import AddDuplicationRule from './AddDuplicationRule'
import { useSnackbar } from '../../../../contexts/SnackbarProvider.jsx'
import ColumnConfiguration from '../../../common/columnConfiguration.jsx'
import {
  addDuplicationRule,
  getDuplicationRule,
  updateDuplicationRule,
  downloadDuplicationRule,
} from '../../../../api/duplicationRule.js'
import { saveBlobAsFile } from '../../../../api/apiUtils.js'
import { fieldOptions } from '../../Define/configs/ruleSettingTableConfig'
import { DEFAULT_PAGE, sanitizeForLog } from '../../../../constants'

const DuplicationRule = ({ roleConfig }) => {
  const [duplicationRulesList, setduplicationRulesList] = useState([])
  const [showTable, setShowTable] = useState(false)
  const [showAddDuplicationRule, setShowAddDuplicationRule] = useState(false)
  const [loading, setLoading] = useState(false)
  const [startDownloading, setStartDownloading] = useState(false)
  const [duplicationRuleForm, setDuplicationRuleForm] = useState({
    ruleName: '',
    parameters: [''],
    ruleStatus: 'ACTIVE',
  })
  const [editUuid, setEditUuid] = useState(null)
  const [formErrors, setFormErrors] = useState({})
  const [tanMasterUuid, setTanMasterUuid] = useState(null)
  const [addDialogOpen, setaddDialogOpen] = useState(false)

  // pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [totalItems, setTotalItems] = useState(0)

  // column configuration
  const [columnConfigOpen, setColumnConfigOpen] = useState(false)
  const [columnConfigAnchor, setColumnConfigAnchor] = useState(null)
  const [visibleColumns, setVisibleColumns] = useState(
    columns.map(col => col.id)
  )
  const filteredColumns = useMemo(
    () => columns.filter(col => visibleColumns.includes(col.id)),
    [visibleColumns]
  )

  const { showSnackbar } = useSnackbar()

  const fetchDuplicationRule = async (
    page = 1,
    pageSize = itemsPerPage,
    searchList = [],
    tandID = tanMasterId
  ) => {
    setLoading(true)
    const payload = {
      pageFilter: {
        currentPage: page,
        includeCount: true,
        pageSize: pageSize,
        sortField: 'createdDate',
        sortOrder: 'DESC',
      },
      requestFilter: [],
      isReportDownload: false,
      searchCOList: searchList,
    }
    try {
      const response = await getDuplicationRule(payload, tandID)

      if (response.success) {
        const responseData = response.data.content || []
        setduplicationRulesList(responseData)
        setTotalItems(response.data.totalElements)
      } else {
        console.error('Failed to load entities:')
        setduplicationRulesList([])
      }
    } catch (error) {
      console.error('Error loading entities:')
      setduplicationRulesList([])
    } finally {
      setLoading(false)
    }
  }

  const [saveState, saveAction, isSaving] = useActionState(
    async (prev, payload) => {
      console.log(payload)

      const isEdit = !!payload.uuid
      const res = isEdit
        ? await updateDuplicationRule(payload, payload.uuid)
        : await addDuplicationRule(payload)

      if (res.success) {
        showSnackbar(
          isEdit ? 'Rule updated successfully!' : 'Rule added successfully!',
          'success'
        )
        fetchDuplicationRule(currentPage, itemsPerPage, [], tanMasterUuid)
        setShowAddDuplicationRule(false)
        setaddDialogOpen(false)
        return null
      } else {
        showSnackbar(res.message || 'Failed', 'error')
        return res
      }
    },
    null
  )

  const handleViewClick = data => {
    setTanMasterUuid(data.tanMasterUuid)
    fetchDuplicationRule(1, itemsPerPage, [], data.tanMasterUuid)
    setShowTable(true)
  }

  const handleAddDialog = () => {
    setaddDialogOpen(true)
    setDuplicationRuleForm({
      ruleName: '',
      parameters: [''],
      ruleStatus: 'ACTIVE',
    })
  }

  const handleAddRuleCancel = () => {
    setShowAddDuplicationRule(false)
    setaddDialogOpen(false)
    setFormErrors({})
    setDuplicationRuleForm({
      ruleName: '',
      parameters: [''],
      ruleStatus: 'ACTIVE',
    })
  }

  const handleColumnConfigOpen = event => {
    setColumnConfigAnchor(event.currentTarget)
    setColumnConfigOpen(!columnConfigOpen)
  }

  const handleColumnChange = selectedColumns => {
    setVisibleColumns(selectedColumns)
  }

  const handleColumnConfigurationClose = () => {
    setColumnConfigOpen(false)
    setColumnConfigAnchor(null)
  }

  const handleSave = validatedData => {
    const isEdit = !!validatedData.uuid

    const payload = {
      ruleName: validatedData.ruleName,
      ruleStatus: validatedData.ruleStatus,
      parameters: validatedData.parameters,
      tanMasterUuid,
      ...(isEdit && { uuid: validatedData.uuid }),
    }

    startTransition(() => {
      saveAction(payload)
    })
  }

  const handlePageChange = page => {
    setCurrentPage(page)
    fetchDuplicationRule(page, itemsPerPage, [], tanMasterUuid)
  }

  const handleItemsPerPageChange = newItemsPerPage => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
    fetchDuplicationRule(1, newItemsPerPage, [], tanMasterUuid)
  }
  const mapLabelToValue = (label) => {
    const found = fieldOptions.find(f => f.label.toLowerCase() === label?.toLowerCase());
    return found ? found.value : label; // fallback if not found
  };

  const handleEdit = rowData => {
    setEditUuid(rowData.uuid);

    const params = [
      rowData.parameter_1,
      rowData.parameter_2,
      rowData.parameter_3,
      rowData.parameter_4,
      rowData.parameter_5,
      rowData.parameter_6,
      rowData.parameter_7,
      rowData.parameter_8,
    ]
      .filter(Boolean)
      .map(p => mapLabelToValue(p));  // <-- MAGIC FIX

    setDuplicationRuleForm({
      ruleName: rowData.ruleName || '',
      ruleStatus: rowData.ruleStatus?.toUpperCase() || 'ACTIVE',
      parameters: params,
      uuid: rowData.uuid,
    });

    setShowAddDuplicationRule(true);
  };



  const handleDownload = async () => {
    if (totalItems === 0) {
      showSnackbar('No Data Found', 'info')
      return
    }
    const payload = {
      pageFilter: {
        currentPage: 1,
        includeCount: true,
        pageSize: DEFAULT_PAGE,
        sortField: 'createdDate',
        sortOrder: 'DESC',
      },
      requestFilter: [],
      isReportDownload: false,
      searchCOList: [],
    }
    const result = await downloadDuplicationRule(payload, tanMasterUuid)
    if (result?.success && result?.data) {
      saveBlobAsFile(result.data, 'duplication_rules.xlsx')
    } else if (result?.error) {
      showSnackbar(result.error, 'error')
    }
  }

  return (
    <Box>
      {/* Filter Section */}
      <KeyFilter handleViewClick={handleViewClick} />

      {/* Table Section */}
      {showTable && (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              minHeight: '32px',
              py: '12px',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: '20px',
                color: 'var(--forbrand-secondary)',
                fontWeight: 500,
              }}
            >
              Duplication Rule
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Column Configuration">
                <IconButton
                  sx={{ color: 'var(--forbrand-primary)' }}
                  onClick={handleColumnConfigOpen}
                >
                  <ColumnConfigurationIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Download Excel">
                <span className="disabled:cursor-not-allowed hover:bg-[#e3f2fd] rounded-full">
                  <IconButton
                    disabled={loading || startDownloading}
                    onClick={handleDownload}
                  >
                    <DownloadIcon />
                  </IconButton>
                </span>
              </Tooltip>

              {roleConfig.canCreate && (
                <>
                  <Typography
                    sx={{
                      height: '16px',
                      borderLeft: '1px solid var(--forbrand-color)',
                      marginRight: '6px',
                    }}
                  ></Typography>

                  <Button
                    variant="contained"
                    onClick={handleAddDialog}
                    sx={{
                      backgroundColor: 'var(--forbrand-bg-variant)',
                      color: 'var(--forbrand-brand-varient)',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '16px',
                    }}
                  >
                    Add
                  </Button>
                </>
              )}
            </Box>
          </Box>

          <Box>
            <CommonTable
              columns={filteredColumns}
              data={duplicationRulesList}
              loading={loading}
              emptyMessage="No Rule Found"
              showFooter={true}
              totalItems={totalItems}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              showItemsPerPage={true}
              showTotalItems={true}
              showPageNumbers={true}
              showActions={true}
              onPageChange={handlePageChange}
              onEdit={roleConfig.canCreate && handleEdit}
              onItemsPerPageChange={handleItemsPerPageChange}
              disabledEdit={true}
              currentTable={'tableUsingRuleStatus'}
            />
          </Box>
        </>
      )}

      {/* Add Section Dialog */}
      <CommonModal
        open={showAddDuplicationRule}
        onClose={handleAddRuleCancel}
        showCloseButton={true}
        fullScreen={true}
        maxWidth={'lg'}
        sx={{
          '& .MuiDialogContent-root': { padding: 0 },
          '& .MuiDialogTitle-root': { display: 'none' },
        }}
      >
        <AddDuplicationRule
          open={showAddDuplicationRule}
          onCancel={handleAddRuleCancel}
          formData={duplicationRuleForm}
          setFormData={setDuplicationRuleForm}
          formErrors={formErrors}
          setFormErrors={setFormErrors}
          onSave={handleSave}
          isEdit={editUuid}
        />
      </CommonModal>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={addDialogOpen}
        onConfirm={() => {
          setFormErrors({})
          setShowAddDuplicationRule(true)
        }}
        title="Active duplication rule found!"
        message={`On creating a new duplication rule, existing rule will get "Inactive".Do you want to continue?`}
        confirmText="Proceed"
        itemType="rule"
        type="info"
        showCancelButton={true}
        onCancel={() => setaddDialogOpen(false)}
      />

      <ColumnConfiguration
        columns={columns}
        visibleColumns={visibleColumns}
        anchorEl={columnConfigAnchor}
        open={columnConfigOpen}
        onColumnChange={handleColumnChange}
        onClose={handleColumnConfigurationClose}
      />
    </Box>
  )
}

export default DuplicationRule
